import { useEffect, useState } from 'react';
import { listMessages, markMessageRead, deleteMessage } from '../adminApi';
import { toast } from 'sonner';
import { Mail, MailOpen, Trash2, ChevronDown, ChevronUp } from 'lucide-react';

export default function MessagesPage() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    listMessages()
      .then(setMessages)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleRead = async (id) => {
    try {
      await markMessageRead(id);
      setMessages((ms) => ms.map((m) => (m.id === id ? { ...m, is_read: true } : m)));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteMessage(id);
      setMessages((ms) => ms.filter((m) => m.id !== id));
      setDeleteId(null);
      toast.success('Mesaj silindi.');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const unreadCount = messages.filter((m) => !m.is_read).length;

  if (loading) return <PageLoader />;
  if (error) return <PageError message={error} />;

  return (
    <div className="p-4 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-white tracking-tight">Mesajlar</h1>
          <p className="text-white/35 text-sm mt-0.5">
            {messages.length} mesaj
            {unreadCount > 0 && (
              <span className="ml-2 text-[#c5a059]">{unreadCount} okunmamış</span>
            )}
          </p>
        </div>
      </div>

      {messages.length === 0 ? (
        <EmptyState icon={Mail} text="Henüz mesaj yok." />
      ) : (
        <div className="space-y-2">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`bg-[#111] border rounded-xl overflow-hidden transition-colors ${
                msg.is_read ? 'border-white/[0.06]' : 'border-[#c5a059]/20'
              }`}
            >
              {/* Header row */}
              <div className="flex items-center gap-4 px-5 py-4">
                <div className="flex-shrink-0">
                  {msg.is_read ? (
                    <MailOpen size={15} className="text-white/25" />
                  ) : (
                    <Mail size={15} className="text-[#c5a059]" />
                  )}
                </div>

                <div className="flex-1 min-w-0 grid grid-cols-[1fr_auto] sm:grid-cols-12 gap-2 sm:gap-3 items-center">
                  <span className="sm:col-span-3 text-sm font-medium text-white truncate">
                    {msg.name}
                  </span>
                  <span className="hidden sm:block sm:col-span-3 text-sm text-white/45 truncate">{msg.email}</span>
                  <span className="hidden sm:block sm:col-span-4 text-sm text-white/55 truncate">
                    {msg.subject || msg.message?.substring(0, 60) + '…'}
                  </span>
                  <span className="sm:col-span-2 text-xs text-white/25 text-right">
                    {msg.created_at ? new Date(msg.created_at).toLocaleDateString('tr-TR') : '—'}
                  </span>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                  {!msg.is_read && (
                    <button
                      onClick={() => handleRead(msg.id)}
                      title="Okundu işaretle"
                      className="p-1.5 rounded-lg text-white/35 hover:text-[#c5a059] hover:bg-[#c5a059]/10 transition-colors"
                    >
                      <MailOpen size={14} />
                    </button>
                  )}

                  {deleteId === msg.id ? (
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => handleDelete(msg.id)}
                        className="text-xs bg-red-500/15 text-red-400 border border-red-500/20 px-2.5 py-1 rounded-lg hover:bg-red-500/25 transition-colors"
                      >
                        Evet, sil
                      </button>
                      <button
                        onClick={() => setDeleteId(null)}
                        className="text-xs text-white/35 hover:text-white/60 px-2 py-1"
                      >
                        İptal
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteId(msg.id)}
                      title="Sil"
                      className="p-1.5 rounded-lg text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}

                  <button
                    onClick={() => setExpanded(expanded === msg.id ? null : msg.id)}
                    className="p-1.5 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/[0.04] transition-colors"
                  >
                    {expanded === msg.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                </div>
              </div>

              {/* Expanded body */}
              {expanded === msg.id && (
                <div className="px-5 pb-4 border-t border-white/[0.05]">
                  <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
                    {msg.phone && (
                      <Field label="Telefon" value={msg.phone} />
                    )}
                    {msg.subject && <Field label="Konu" value={msg.subject} />}
                  </div>
                  <div>
                    <p className="text-[10px] text-white/25 uppercase tracking-wider mb-1.5">Mesaj</p>
                    <p className="text-sm text-white/70 leading-relaxed whitespace-pre-wrap">
                      {msg.message}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[10px] text-white/25 uppercase tracking-wider mb-1">{label}</p>
      <p className="text-sm text-white/65">{value}</p>
    </div>
  );
}

function PageLoader() {
  return (
    <div className="p-8 flex items-center justify-center min-h-64">
      <div className="w-5 h-5 border-2 border-[#c5a059]/30 border-t-[#c5a059] rounded-full animate-spin" />
    </div>
  );
}

function PageError({ message }) {
  return (
    <div className="p-8">
      <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-5 py-4 text-red-400 text-sm">
        Hata: {message}
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-white/20">
      <Icon size={32} className="mb-3" />
      <p className="text-sm">{text}</p>
    </div>
  );
}
